import {Button, Card, Col, Modal, Row} from 'react-bootstrap'
import {Fragment, useEffect, useMemo, useRef, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {copyToClipboard} from '../../../../../utils/clipboardUtils'
import {GET_ONE_POLICY_API} from '../../../gql/queries/policiesApiQueries'
import {GET_MARKET_PLACE_2} from '../../../gql/queries/leadsQuery'
import {IResOnePolicyApi, NodePolicy} from '../../../types/policiesApi'
import {IResTest} from '../../../types/leads'
import {Plan} from '../../oneLead/oneleadTypes'
import {parseId} from '../../../helpers'
import {getMetalLevelValue} from '../../../helpers/consts'
import {
  buildMarketplaceLookupVariables,
  buildPolicyCardData,
  canLookupMarketplaceCardData,
  parseMarketplacePlanResponse,
} from './idCardData'
import {openPrintableMemberIdCard} from './idCardPrint'

interface ModalMemberIdCardProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

export const ModalMemberIdCard: React.FC<ModalMemberIdCardProps> = ({isOpen, onClose, id}) => {
  const [info, setInfo] = useState<NodePolicy | null>(null)
  const [marketPlan, setMarketPlan] = useState<Plan | null>(null)
  const [marketLookupError, setMarketLookupError] = useState<string | null>(null)
  const lookupKeyRef = useRef<string>('')

  const [gqlGetPolicy, {data: dataPolicy, loading: loadingPolicy, error: errorPolicy}] =
    useLazyQuery<IResOnePolicyApi>(GET_ONE_POLICY_API, {fetchPolicy: 'no-cache'})
  const [gqlGetMarketplace, {data: dataMarketplace, loading: loadingMarketplace}] =
    useLazyQuery<IResTest>(GET_MARKET_PLACE_2, {fetchPolicy: 'no-cache'})

  useEffect(() => {
    if (!isOpen || !id) return
    gqlGetPolicy({variables: {id: parseId(id)}})
  }, [gqlGetPolicy, id, isOpen])

  useEffect(() => {
    if (!dataPolicy?.policiesById) return
    setInfo(dataPolicy.policiesById)
    setMarketPlan(null)
    setMarketLookupError(null)
    lookupKeyRef.current = ''
  }, [dataPolicy])

  useEffect(() => {
    if (!dataMarketplace?.marketplace2) return
    const {plan, errorMessage} = parseMarketplacePlanResponse(dataMarketplace.marketplace2, {
      planName: info?.planName,
      issuerName: info?.carrier?.name,
      metalLevel: info ? getMetalLevelValue(String(info.metalLevel || '')) : '',
    })
    setMarketPlan(plan)
    setMarketLookupError(errorMessage)
  }, [dataMarketplace, info])

  useEffect(() => {
    if (!info) return
    const variables = buildMarketplaceLookupVariables(info)
    if (!variables) return

    const lookupKey = JSON.stringify({
      policyId: info.id,
      cmsPlanId: info.CMSPlanId,
      planName: info.planName,
      carrier: info.carrier?.name,
      metalLevel: info.metalLevel,
      state: info.stateEU?.code,
      effectiveDate: info.effectiveDate,
      membersCount: info.memberPolicySet?.edges?.length || 0,
    })
    if (lookupKeyRef.current === lookupKey) return

    lookupKeyRef.current = lookupKey
    setMarketLookupError(null)
    gqlGetMarketplace({variables})
  }, [gqlGetMarketplace, info])

  const cardData = useMemo(() => {
    if (!info) return null
    return buildPolicyCardData(info, marketPlan)
  }, [info, marketPlan])

  const canLookupMarketplace = info ? canLookupMarketplaceCardData(info) : false

  return (
    <Modal show={isOpen} onHide={onClose} backdrop='static' keyboard={false} dialogClassName='modal-wide-sm'>
      <Modal.Header closeButton>
        <Modal.Title>Get member ID card</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{backgroundColor: '#f2f3f8'}} className='modal-body-policy-preview'>
        {loadingPolicy && <div className='text-center'>Loading policy...</div>}
        {errorPolicy && <div className='text-center text-danger'>Error loading policy data.</div>}
        {!loadingPolicy && !errorPolicy && cardData && (
          <Card className='policy-id-card-information'>
            <Card.Body>
              <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
                <div className='small text-muted'>
                  Source:{' '}
                  <strong>
                    {cardData.source === 'LOCAL_AND_MARKETPLACE'
                      ? 'Policy + Marketplace'
                      : 'Policy only'}
                  </strong>
                </div>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => copyToClipboard(JSON.stringify(cardData, null, 2), 'ID card data JSON')}
                >
                  Copy card JSON
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => {
                    const opened = openPrintableMemberIdCard(cardData)
                    if (!opened) toast.error('Please allow pop-ups to print or save the card as PDF.')
                  }}
                >
                  Print / Save PDF
                </Button>
              </div>
              {loadingMarketplace && canLookupMarketplace && (
                <div className='small text-info mb-2'>Loading marketplace plan details...</div>
              )}
              {marketLookupError && (
                <div className='small text-warning mb-2'>
                  Marketplace lookup failed: {marketLookupError}. Showing local fields only.
                </div>
              )}
              {!canLookupMarketplace && info?.CMSPlanId && (
                <div className='small text-warning mb-2'>
                  CMS Plan lookup is only available when state jurisdiction is <strong>FEDERAL</strong>.
                </div>
              )}
              <Row className='g-3'>
                <Col md={6}>
                  <div className='id-card-box'>
                    <div className='id-card-box__title'>Front</div>
                    <div className='id-card-grid'>
                      <div>Subscriber</div>
                      <div>{cardData.subscriberName}</div>
                      <div>Member</div>
                      <div>{cardData.memberName}</div>
                      <div>Member ID</div>
                      <div>{cardData.memberId}</div>
                      <div>Policy #</div>
                      <div>{cardData.policyId}</div>
                      <div>Plan</div>
                      <div>{cardData.planName}</div>
                      <div>Effective Date</div>
                      <div>{cardData.effectiveDate}</div>
                      <div>Type / Metal</div>
                      <div>
                        {cardData.planType} / {cardData.metalLevel}
                      </div>
                      <div>Deductible (Ind/Fam)</div>
                      <div>{cardData.medicalDeductible}</div>
                      <div>OOPM (Ind/Fam)</div>
                      <div>{cardData.annualOopm}</div>
                      <div>PCP</div>
                      <div>{cardData.pcp}</div>
                      <div>Specialist</div>
                      <div>{cardData.specialist}</div>
                      <div>Urgent Care</div>
                      <div>{cardData.urgentCare}</div>
                      <div>ER</div>
                      <div>{cardData.emergencyRoom}</div>
                      <div>Rx Generic / Brand</div>
                      <div>
                        {cardData.genericRx} / {cardData.preferredBrandRx}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className='id-card-box'>
                    <div className='id-card-box__title'>Back</div>
                    <div className='id-card-grid'>
                      <div>Carrier</div>
                      <div>{cardData.carrierName}</div>
                      <div>Member Services</div>
                      <div>{cardData.memberServicesPhone}</div>
                      <div>Provider</div>
                      <div>{cardData.providerPhone}</div>
                      <div>Pharmacy</div>
                      <div>{cardData.pharmacyPhone}</div>
                      <div>Nurse Line</div>
                      <div>{cardData.nurseLine}</div>
                      <div>Billing</div>
                      <div>{cardData.billingPhone}</div>
                      <div>Provider Help Desk</div>
                      <div>{cardData.providerHelpDesk}</div>
                      <div>Prior Auth</div>
                      <div>{cardData.priorAuthPhone}</div>
                      <div>Care Team</div>
                      <div>{cardData.careTeamPhone}</div>
                      <div>Mental Health</div>
                      <div>{cardData.mentalHealthPhone}</div>
                      <div>Medical Claims</div>
                      <div>{cardData.medicalClaimsAddress}</div>
                      <div>Pharmacy Claims</div>
                      <div>{cardData.pharmacyClaimsAddress}</div>
                      <div>Claims - Mental Health</div>
                      <div>{cardData.claimsMentalHealth}</div>
                      <div>Claims - Pharmacy</div>
                      <div>{cardData.claimsPharmacy}</div>
                      <div>Claims - Pediatric Vision</div>
                      <div>{cardData.claimsPediatricVision}</div>
                      <div>Claims - Medical</div>
                      <div>{cardData.claimsMedical}</div>
                      <div>Payer ID</div>
                      <div>{cardData.payerId}</div>
                      <div>Website</div>
                      <div>{cardData.website}</div>
                      <div>Provider Portal</div>
                      <div>{cardData.providerPortal}</div>
                      <div>Issuer TTY</div>
                      <div>{cardData.issuerTty}</div>
                      <div>Referral</div>
                      <div>{cardData.referralRequired}</div>
                      <div>Rx BIN</div>
                      <div>{cardData.rxBin}</div>
                      <div>Rx PCN</div>
                      <div>{cardData.rxPcn}</div>
                      <div>Rx GRP</div>
                      <div>{cardData.rxGrp}</div>
                      <div>Benefits URL</div>
                      <div>{cardData.benefitsUrl}</div>
                      <div>Formulary URL</div>
                      <div>{cardData.formularyUrl}</div>
                      <div>Network URL</div>
                      <div>{cardData.providerNetworkUrl}</div>
                      {cardData.additionalBackLines.map((line, index) => (
                        <Fragment key={`extra-back-${index}`}>
                          <div>Note {index + 1}</div>
                          <div>{line}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
